import { TileNode } from '@/components/diagrams/FlowChart';
import { isTileNode } from '@/components/diagrams/FlowChart/utils/nodes';
import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';

import { EntityDiagramActionFn, UpdateTileConfigAction } from '../types';

export const updateTileConfigAction: EntityDiagramActionFn<
  UpdateTileConfigAction
> = (state, action) => {
  const { nodeId, notes, componentType, iconShape } = action;

  const node = state.nodes.find((n) => n.id === nodeId);
  if (!node || !isTileNode(node)) return state;

  // Reset height when content/layout-affecting fields change so the tile
  // re-measures to its new natural size. Pure visual swaps (e.g. emphasis)
  // wouldn't, but this modal doesn't edit emphasis.
  const nextComponentType = componentType ?? node.data.componentType;
  const isIconTile = nextComponentType === GraphVizNodeComponentType.TileIcon;
  const componentTypeChanged =
    componentType !== undefined && node.data.componentType !== componentType;
  const iconShapeChanged =
    isIconTile && iconShape !== undefined && node.data.iconShape !== iconShape;
  const existingNotesLength =
    typeof node.data.notes === 'string' ? node.data.notes.length : 0;
  const notesChangedLength =
    notes !== undefined && notes.length !== existingNotesLength;
  const resetHeight =
    componentTypeChanged || iconShapeChanged || notesChangedLength;

  const updatedNode: TileNode = {
    ...node,
    ...(resetHeight && {
      width: node.width,
      height: undefined,
      measured: { width: node.measured?.width, height: undefined },
    }),
    data: {
      ...node.data,
      ...(resetHeight && { dimensionsFinalized: undefined }),
      // Each field guards on `!== undefined` so a caller that only supplies
      // a subset (e.g. a future emphasis-only dispatcher) doesn't clobber
      // the unspecified fields back to undefined.
      ...(notes !== undefined && { notes }),
      ...(componentType !== undefined && { componentType }),
      ...(iconShape !== undefined && { iconShape }),
    },
  };

  return {
    ...state,
    nodes: state.nodes.map((n) => (n.id === nodeId ? updatedNode : n)),
  };
};
